import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from 'react-redux';
import { AsyncPaginate } from "react-select-async-paginate";

import { search, searchFocusedToggle } from '../../../../actions';
import { getSpecializations } from '../../../../actions/doctors';
import * as api from '../../../../api';
import { isMobile } from '../../../../utils/mobile';

import { useHistory } from "react-router-dom";

const SearchForm = ({ user, searchFilters, setSearchFilters, patient }) => {
  const asyncPaginateMenuProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: {
      menuPortal: (base) => ({
        ...base,
        zIndex: 2000,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 2000,
      }),
      singleValue: (base) => ({
        ...base,
        color: '#1767c8',
      }),
    },
  };
  
  const [valueInstitution, setValueInstitution] = useState(null);
  const [valueDivision, setValueDivision] = useState(null);
  const [valueSpecialization, setValueSpecialization] = useState(null);
  const [valueDoctor, setValueDoctor] = useState(null);
  const [visitDate, setVisitDate] = useState("");

  const [selecteOpened, setSelecteOpened] = useState(false);

  const [institutionSearchQuery, setInstitutionSearchQuery] = useState("");
  const [divisionSearchQuery, setDivisionSearchQuery] = useState("");
  const [specializationSearchQuery, setSpecializationSearchQuery] = useState("");
  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");
    
  const [filters, setFilters] = useState({
    byReferral: false,
    paidConsultations: false,
    onlyAvailable: false,
    screening_40_plus: false,
    defencer_benefit: false,
  });

  // Mobile modal states
  const [showMobileModal, setShowMobileModal] = useState(null);
  const [mobileOptions, setMobileOptions] = useState([]);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [mobilePage, setMobilePage] = useState(1);
  const [mobileHasMore, setMobileHasMore] = useState(true);

  // Keyword field states
  const [keyword, setKeyword] = useState(searchFilters?.keyword || '');
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  const [popularSearches, setPopularSearches] = useState([]);
  const [popularSearchesLoading, setPopularSearchesLoading] = useState(false);
  const [popularSearchesPage, setPopularSearchesPage] = useState(1);
  const [popularSearchesHasMore, setPopularSearchesHasMore] = useState(true);
  const keywordRef = useRef(null);
  const keywordDebounceRef = useRef(null);

  const [isAdvancedSearch, setIsAdvancedSearch] = useState(() => {
    try {
      return localStorage.getItem('searchForm_advancedMode') === 'true';
    } catch { return false; }
  });

  const toggleAdvancedSearch = (value) => {
    setIsAdvancedSearch(value);
    try {
      localStorage.setItem('searchForm_advancedMode', String(value));
    } catch {}
  };

  const history = useHistory();

  // LocalStorage functions
  const saveToLocalStorage = (key, value) => {
    try {
      localStorage.setItem(`searchForm_${key}`, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const loadFromLocalStorage = (key) => {
    try {
      const item = localStorage.getItem(`searchForm_${key}`);
      if (item === null) return null; // Key doesn't exist
      
      const parsed = JSON.parse(item);
      // For doctor field, distinguish between null (user cleared) and undefined (not set)
      if (key === 'doctor' && parsed === null) {
        return 'USER_CLEARED'; // Special value to indicate user intentionally cleared
      }
      return parsed;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  };

  const clearFromLocalStorage = (key) => {
    try {
      // For doctor field, save null instead of removing to indicate user choice
      if (key === 'doctor') {
        localStorage.setItem(`searchForm_${key}`, JSON.stringify(null));
      } else {
        localStorage.removeItem(`searchForm_${key}`);
      }
    } catch (error) {
      console.error('Error clearing from localStorage:', error);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 28);
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const day = String(maxDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    setValueInstitution("");
  }, [user.city]);

  const loadOptionsInstitution = async (searchQuery, loadedOptions, { page }) => {
    const cityId = user.city?.id || '';
    const regionId = user.region?.id || '';
    const query = `?city_id=${cityId}&region_id=${regionId}&name=${searchQuery}&page=${page}`;
    const responseJSON = await api.getHospitals(query);

    return {
      options: responseJSON.data,
      hasMore: responseJSON.pagination.current_page < responseJSON.pagination.total_pages,
      additional: {
        page: page+1,
      },
    };
  };

  const loadOptionsDivisions = async (searchQuery, loadedOptions, { page }) => {
    const institutionId = valueInstitution?.id || '';
    if (!institutionId) {
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 },
      };
    }
    const query = `?city_id=${user.city?.id || ''}&name=${searchQuery}&institution_id=${institutionId}&page=${page}`;
    const responseJSON = await api.getDivisions({ cityId: user.city?.id || '', name: searchQuery, institutionId, page });

    return {
      options: responseJSON.data,
      hasMore: responseJSON.pagination.current_page < responseJSON.pagination.total_pages,
      additional: {
        page: page+1,
      },
    };
  };

  const loadOptionsSpecializations = async (searchQuery, loadedOptions, { page }) => {
    const institutionId = valueInstitution?.id || '';
    const divisionId = valueDivision?.id || '';
    const query = `?city_id=${user.city?.id || ''}&name=${searchQuery}&has_doctors=1&institution_id=${institutionId}&division_id=${divisionId}&page=${page}`;
    const responseJSON = await api.getSpecializations(query);

    return {
      options: responseJSON.data,
      hasMore: responseJSON.pagination.current_page < responseJSON.pagination.total_pages,
      additional: {
        page: page+1,
      },
    };
  };

  const loadOptionsDoctors = async (searchQuery, loadedOptions, { page }) => {
    const specializationId = valueSpecialization?.id || '';
    const institutionId = valueInstitution?.id || '';
    const divisionId = valueDivision?.id || '';

    const query = `?city_id=${user.city?.id || ''}&name=${searchQuery}&institution_id=${institutionId}&division_id=${divisionId}&specialization_id=${specializationId}&page=${page}`;
    const responseJSON = await api.searchDoctors(query);

    return {
      options: responseJSON.data,
      hasMore: responseJSON.pagination.current_page < responseJSON.pagination.total_pages,
      additional: {
        page: page+1,
      },
    };
  };

  const onChangeInstitution = (option) => {
    setValueInstitution(option);
    setValueDivision(null);
    setValueSpecialization("");
    setValueDoctor("");
    if(option !== null) {
      saveToLocalStorage('institution', option);
    } else {
      sessionStorage.removeItem('institutionSearchQuery');
      clearFromLocalStorage('institution');
    }
    clearFromLocalStorage('division');
    clearFromLocalStorage('specialization');
    clearFromLocalStorage('doctor');
  };
  
  const onChangeDivision = (option) => {
    setValueDivision(option);
    setValueSpecialization("");
    setValueDoctor("");
    if(option !== null) {
      saveToLocalStorage('division', option);
    } else {
      sessionStorage.removeItem('divisionSearchQuery');
      clearFromLocalStorage('division');
    }
    clearFromLocalStorage('specialization');
    clearFromLocalStorage('doctor');
  };
  
  const onChangeSpecialization = (option) => {
    setValueSpecialization(option);
    if(option !== null) {
      setValueDoctor("");
      saveToLocalStorage('specialization', option);
    } else {
      sessionStorage.removeItem('specializationSearchQuery');
      clearFromLocalStorage('specialization');
    }
  };
  
  const onChangeDoctor = (option) => {
    setValueDoctor(option);
    if(option === null) {
      sessionStorage.removeItem('doctorSearchQuery');
      clearFromLocalStorage('doctor');
    } else {
      saveToLocalStorage('doctor', option);
    }
  };

  const onInstitutionMenuOpen = () => {
      setSelecteOpened(true);
      const savedQuery = sessionStorage.getItem('institutionSearchQuery') || '';
      if (savedQuery && institutionSearchQuery === '') {
        setInstitutionSearchQuery(savedQuery);
      }
  };

  const onInstitutionMenuClose = () => {
    if(selecteOpened) {
      sessionStorage.setItem('institutionSearchQuery', institutionSearchQuery);
      setSelecteOpened(false);
    }
  };

  const onDivisionMenuOpen = () => {
    setSelecteOpened(true);
    const savedQuery = sessionStorage.getItem('divisionSearchQuery') || '';
    if (savedQuery && divisionSearchQuery === '') {
      setDivisionSearchQuery(savedQuery);
    }
  };

  const onDivisionMenuClose = () => {
    if(selecteOpened) {
      sessionStorage.setItem('divisionSearchQuery', divisionSearchQuery);
      setSelecteOpened(false);
    }
  };

  const onSpecializationMenuOpen = () => {
    setSelecteOpened(true);
    const savedQuery = sessionStorage.getItem('specializationSearchQuery') || '';
    if (savedQuery && specializationSearchQuery === '') {
      setSpecializationSearchQuery(savedQuery);
    }
  };

  const onSpecializationMenuClose = () => {
    if(selecteOpened) {
      sessionStorage.setItem('specializationSearchQuery', specializationSearchQuery);
      setSelecteOpened(false);
    }
  };

  const onDoctorMenuOpen = () => {
    setSelecteOpened(true);
    const savedQuery = sessionStorage.getItem('doctorSearchQuery') || '';
    if (savedQuery && doctorSearchQuery === '') {
      setDoctorSearchQuery(savedQuery);
    }
  };

  const onDoctorMenuClose = () => {
    if(selecteOpened) {
      sessionStorage.setItem('doctorSearchQuery', doctorSearchQuery);
      setSelecteOpened(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, checked } = event.target;
    let newFilters = {
      ...filters,
      [name]: checked
    };
    
    // If screening_40_plus is checked, uncheck byReferral
    if (name === 'screening_40_plus' && checked) {
      newFilters.byReferral = false;
    }
    
    setFilters(newFilters);
    saveToLocalStorage('filters', newFilters);
  };

  // Mobile modal functions
  const openMobileModal = (type) => {
    setShowMobileModal(type);
    setMobileOptions([]);
    setMobileSearchQuery('');
    setMobilePage(1);
    setMobileHasMore(true);
    loadMobileOptions(type, '', 1);
  };

  const closeMobileModal = () => {
    setShowMobileModal(null);
    setMobileOptions([]);
    setMobileSearchQuery('');
  };

  const loadMobileOptions = async (type, searchQuery = '', page = 1) => {
    if (page === 1) {
      setMobileLoading(true);
    }

    try {
      let responseJSON;
      
      switch (type) {
        case 'institution':
          const institutionQuery = `?city_id=${user.city?.id || ''}&name=${searchQuery}&page=${page}`;
          responseJSON = await api.getHospitals(institutionQuery);
          break;
        case 'division':
          const instId = valueInstitution?.id || '';
          if (!instId) {
            responseJSON = { data: [], pagination: { current_page: 1, total_pages: 1 } };
            break;
          }
          responseJSON = await api.getDivisions({ cityId: user.city?.id || '', name: searchQuery, institutionId: instId, page });
          break;
        case 'specialization':
          const institutionId = valueInstitution?.id || '';
          const divisionId = valueDivision?.id || '';
          const specializationQuery = `?city_id=${user.city?.id || ''}&name=${searchQuery}&has_doctors=1&institution_id=${institutionId}&division_id=${divisionId}&page=${page}`;
          responseJSON = await api.getSpecializations(specializationQuery);
          break;
        case 'doctor':
          const specId = valueSpecialization?.id || '';
          const instIdDoc = valueInstitution?.id || '';
          const divisionIdDoc = valueDivision?.id || '';
          const doctorQuery = `?city_id=${user.city?.id || ''}&name=${searchQuery}&institution_id=${instIdDoc}&division_id=${divisionIdDoc}&specialization_id=${specId}&page=${page}`;
          return;
      }

      if (page === 1) {
        setMobileOptions(responseJSON.data);
      } else {
        setMobileOptions(prev => [...prev, ...responseJSON.data]);
      }
      
      setMobileHasMore(responseJSON.pagination.current_page < responseJSON.pagination.total_pages);
    } catch (error) {
      console.error('Error loading mobile options:', error);
    } finally {
      setMobileLoading(false);
    }
  };

  const handleMobileSearch = (query) => {
    setMobileSearchQuery(query);
    setMobilePage(1);
    loadMobileOptions(showMobileModal, query, 1);
  };

  const loadMoreMobileOptions = () => {
    if (mobileHasMore && !mobileLoading) {
      const nextPage = mobilePage + 1;
      setMobilePage(nextPage);
      loadMobileOptions(showMobileModal, mobileSearchQuery, nextPage);
    }
  };

  const selectMobileOption = (option) => {
    switch (showMobileModal) {
      case 'institution':
        onChangeInstitution(option);
        break;
      case 'division':
        onChangeDivision(option);
        break;
      case 'specialization':
        onChangeSpecialization(option);
        break;
      case 'doctor':
        onChangeDoctor(option);
        break;
    }
    closeMobileModal();
  };

  const clearMobileValue = (type, event) => {
    event.stopPropagation();
    switch (type) {
      case 'institution':
        onChangeInstitution(null);
        break;
      case 'division':
        onChangeDivision(null);
        break;
      case 'specialization':
        onChangeSpecialization(null);
        break;
      case 'doctor':
        onChangeDoctor(null);
        break;
    }
  };

  useEffect(() => {
    if(typeof setSearchFilters === 'function' ) {
      // handleSearch();
    }
  }, [visitDate, valueInstitution, valueDivision, valueSpecialization, valueDoctor, filters]);

  // Load data from localStorage on component mount if searchFilters is undefined
  useEffect(() => {
    if (typeof setSearchFilters === 'undefined') {
      const savedInstitution = loadFromLocalStorage('institution');
      const savedDivision = loadFromLocalStorage('division');
      const savedSpecialization = loadFromLocalStorage('specialization');
      const savedDoctor = loadFromLocalStorage('doctor');
      const savedFilters = loadFromLocalStorage('filters');
      const savedVisitDate = loadFromLocalStorage('visitDate');
      const savedKeyword = loadFromLocalStorage('keyword');

      if (savedInstitution) setValueInstitution(savedInstitution);
      if (savedDivision) setValueDivision(savedDivision);
      if (savedSpecialization) setValueSpecialization(savedSpecialization);
      
      // Handle doctor field with user choice detection
      if (savedDoctor === 'USER_CLEARED') {
        // User intentionally cleared the doctor field, don't load patient doctor
        setValueDoctor(null);
      } else if (savedDoctor) {
        // User has selected a doctor
        setValueDoctor(savedDoctor);
      } else if (patient?.data?.registration?.doctor_id) {
        // No saved doctor and no user choice, load patient's registered doctor
        const loadPatientDoctor = async () => {
          try {
            const doctor = await api.getDoctor(patient.data.registration.doctor_id);
            if (doctor) {
              setValueDoctor(doctor.data);
              saveToLocalStorage('doctor', doctor.data);
            }
          } catch (err) {
            console.error('Failed to load patient doctor:', err);
          }
        };
        loadPatientDoctor();
      }
      
      if (savedFilters) setFilters(savedFilters);
      if (savedVisitDate) setVisitDate(savedVisitDate);
      if (savedKeyword) setKeyword(savedKeyword);
    }
  }, [patient?.data?.registration?.doctor_id]);

  // Save visit date to localStorage
  useEffect(() => {
    if (visitDate) {
      saveToLocalStorage('visitDate', visitDate);
    } else {
      clearFromLocalStorage('visitDate');
    }
  }, [visitDate]);

  // Save keyword to localStorage
  useEffect(() => {
    if (keyword) {
      saveToLocalStorage('keyword', keyword);
    } else {
      clearFromLocalStorage('keyword');
    }
  }, [keyword]);

  const loadPopularSearches = async (query = '', page = 1) => {
    if (page === 1) setPopularSearchesLoading(true);
    try {
      const q = `?query=${encodeURIComponent(query)}&page=${page}`;
      const responseJSON = await api.getPopularSearches(q);
      if (page === 1) {
        setPopularSearches(responseJSON.data);
      } else {
        setPopularSearches(prev => [...prev, ...responseJSON.data]);
      }
      setPopularSearchesHasMore(responseJSON.meta.page < Math.ceil(responseJSON.meta.total / responseJSON.meta.per_page));
      setPopularSearchesPage(page);
    } catch (error) {
      console.error('Error loading popular searches:', error);
    } finally {
      setPopularSearchesLoading(false);
    }
  };

  const onKeywordFocus = () => {
    setShowKeywordDropdown(true);
    loadPopularSearches(keyword, 1);
  };

  const onKeywordChange = (e) => {
    const val = e.target.value;
    setKeyword(val);
    setShowKeywordDropdown(true);
    if (keywordDebounceRef.current) clearTimeout(keywordDebounceRef.current);
    keywordDebounceRef.current = setTimeout(() => {
      loadPopularSearches(val, 1);
    }, 300);
  };

  const loadMorePopularSearches = () => {
    if (popularSearchesHasMore && !popularSearchesLoading) {
      loadPopularSearches(keyword, popularSearchesPage + 1);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (keywordRef.current && !keywordRef.current.contains(e.target)) {
        setShowKeywordDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    // Prepare search parameters based on active mode
    const params = isAdvancedSearch ? {
      institution_id: valueInstitution?.id || null,
      institution_name: valueInstitution?.name || null,
      division_id: valueDivision?.id || null,
      specialization_id: valueSpecialization?.id || null,
      specialization_name: valueSpecialization?.name || null,
      doctor_id: valueDoctor?.id || null,
      doctor_name: valueDoctor?.full_name || null,
      visit_date: visitDate || null,
      byReferral: filters.byReferral,
      paidConsultations: filters.paidConsultations,
      onlyAvailable: filters.onlyAvailable,
      screening_40_plus: filters.screening_40_plus,
      defencer_benefit: filters.defencer_benefit,
      keyword: keyword || null,
      region_id: searchFilters?.region_id || user.region?.id || null,
    } : {
      keyword: keyword || null,
      byReferral: filters.byReferral,
      paidConsultations: filters.paidConsultations,
      onlyAvailable: filters.onlyAvailable,
      screening_40_plus: filters.screening_40_plus,
      defencer_benefit: filters.defencer_benefit,
      region_id: searchFilters?.region_id || user.region?.id || null,
    };
    
    // Update searchFilters first
    if(typeof setSearchFilters === 'function') {
      setSearchFilters(params);
    }
    if (typeof setSearchFilters !== 'function') {
      const cityId = user.city?.id;
      const regionId = user.region?.id;
      const baseUrl = cityId ? `/city/${cityId}/list` : '/list';
      const queryParams = new URLSearchParams();
      
      if (isAdvancedSearch) {
        if (valueInstitution?.id) queryParams.set('institution_id', valueInstitution.id);
        if (valueInstitution?.name) queryParams.set('institution_name', valueInstitution.name);
        if (valueDivision?.id) queryParams.set('division_id', valueDivision.id);
        if (valueSpecialization?.id) queryParams.set('specialization_id', valueSpecialization.id);
        if (valueSpecialization?.name) queryParams.set('specialization_name', valueSpecialization.name);
        if (valueDoctor?.id) queryParams.set('doctor_id', valueDoctor.id);
        if (valueDoctor?.full_name) queryParams.set('doctor_name', valueDoctor.full_name);
        if (visitDate) queryParams.set('visit_date', visitDate);
        if (filters.byReferral) queryParams.set('byReferral', true);
        if (filters.paidConsultations) queryParams.set('paidConsultations', true);
        if (filters.onlyAvailable) queryParams.set('onlyAvailable', true);
        if (filters.screening_40_plus) queryParams.set('screening_40_plus', true);
        if (filters.defencer_benefit) queryParams.set('defencer_benefit', true);
      }
      if (keyword) queryParams.set('keyword', keyword);
      if (!isAdvancedSearch) {
        if (filters.byReferral) queryParams.set('byReferral', true);
        if (filters.paidConsultations) queryParams.set('paidConsultations', true);
        if (filters.onlyAvailable) queryParams.set('onlyAvailable', true);
        if (filters.screening_40_plus) queryParams.set('screening_40_plus', true);
        if (filters.defencer_benefit) queryParams.set('defencer_benefit', true);
      }
      if (!cityId && regionId) queryParams.set('region_id', regionId);
      
      const newUrl = `${baseUrl}?${queryParams.toString()}`;
      history.push(newUrl);
    }
    
  };

  const loadInitialData = useCallback(async () => {
    if (searchFilters) {
      
      // Load institution
      if (searchFilters.institution_id) {
        try {
          const institution = await api.getHospital(searchFilters.institution_id);
          if (institution) setValueInstitution(institution.data);            
        } catch (err) {
          console.error('Failed to load institution:', err);
        }
      }

      // Load division
      if (searchFilters.division_id && searchFilters.institution_id) {
        try {
          const division = await api.getDivision(searchFilters.division_id);
          if (division) setValueDivision(division.data);
        } catch (err) {
          console.error('Failed to load division:', err);
        }
      }

      // Load specialization
      if (searchFilters.specialization_id) {
        try {
      const query = `?city_id=${user.city?.id || ''}&region_id=${user.region?.id || ''}&id=${searchFilters.specialization_id}`;
          const specialization = await api.getSpecializations(query);
          if (specialization) setValueSpecialization(specialization.data[0]);
        } catch (err) {
          console.error('Failed to load specialization:', err);
        }
      }

      // Load doctor
      if (searchFilters.doctor_id) {
        try {
          const doctor = await api.getDoctor(searchFilters.doctor_id);
          if (doctor) setValueDoctor(doctor.data);
        } catch (err) {
          console.error('Failed to load doctor:', err);
        }
      }

      // Set visit date
      if (searchFilters.visit_date) {
        setVisitDate(searchFilters.visit_date);
      }

      // Set filters
      if (searchFilters.byReferral) {
        setFilters(prev => ({
          ...prev,
          byReferral: searchFilters.byReferral === true
        }));
      }
      if (searchFilters.screening_40_plus) {
        setFilters(prev => ({
          ...prev,
          screening_40_plus: searchFilters.screening_40_plus === true
        }));
      }
      if (searchFilters.paidConsultations) {
        setFilters(prev => ({
          ...prev,
          paidConsultations: searchFilters.paidConsultations === true
        }));
      }
      if (searchFilters.onlyAvailable) {
        setFilters(prev => ({
          ...prev,
          onlyAvailable: searchFilters.onlyAvailable === true
        }));
      }
      // Set keyword
      if (searchFilters.keyword) {
        setKeyword(searchFilters.keyword);
      }
    }    
  }, [searchFilters, user?.city?.id]);

  
  useEffect(() => {
    let hasChanges = false;
    if(searchFilters?.visit_date !== undefined) {
      setVisitDate(searchFilters.visit_date);
    }
    if(searchFilters?.institution_id !== undefined && searchFilters?.institution_id !== null && parseInt(searchFilters?.institution_id) !== parseInt(valueInstitution?.id)) {
      setValueInstitution(searchFilters.institution_id);
      hasChanges = true;
    }
    
    if(searchFilters?.division_id !== undefined && searchFilters?.division_id !== null && parseInt(searchFilters?.division_id) !== parseInt(valueDivision?.id)) {
      setValueDivision(searchFilters.division_id);
      hasChanges = true;
    }
    
    if(searchFilters?.specialization_id !== undefined && searchFilters?.specialization_id !== null && parseInt(searchFilters?.specialization_id) !== parseInt(valueSpecialization?.id)) {
      setValueSpecialization(searchFilters.specialization_id);
      hasChanges = true;
    }
    if(searchFilters?.doctor_id !== undefined && searchFilters?.doctor_id !== null && parseInt(searchFilters?.doctor_id) !== parseInt(valueDoctor?.id)) {
      setValueDoctor(searchFilters.doctor_id);
      hasChanges = true;
    }

    // Set filters
    if (searchFilters?.byReferral) {
      setFilters(prev => ({
        ...prev,
        byReferral: searchFilters.byReferral === true
      }));
    }

    if (searchFilters?.screening_40_plus) {
      setFilters(prev => ({
        ...prev,
        screening_40_plus: searchFilters.screening_40_plus === true
      }));
    }
    if (searchFilters?.defencer_benefit) {
      setFilters(prev => ({
        ...prev,
        defencer_benefit: searchFilters.defencer_benefit === true
      }));
    }
    
    if (searchFilters?.paidConsultations) {
      setFilters(prev => ({
        ...prev,
        paidConsultations: searchFilters.paidConsultations === true
      }));
    }
    if (searchFilters?.onlyAvailable) {
      setFilters(prev => ({
        ...prev,
        onlyAvailable: searchFilters.onlyAvailable === true
      }));
    }
    if (searchFilters?.keyword !== undefined) {
      setKeyword(searchFilters.keyword || '');
    }
    if(hasChanges) {
      loadInitialData();
    }

  }, [user?.city?.id, searchFilters]);

  return (
    <div className={`search-form ${isMobile() ? 'mobile' : ''}`}>

      {/* Tabs */}
      <div className="search-form__tabs">
        <button
          type="button"
          className={`search-form__tab ${!isAdvancedSearch ? 'search-form__tab--active' : ''}`}
          onClick={() => toggleAdvancedSearch(false)}
        >
          Пошук
        </button>
        <button
          type="button"
          className={`search-form__tab ${isAdvancedSearch ? 'search-form__tab--active' : ''}`}
          onClick={() => toggleAdvancedSearch(true)}
        >
          Розширений пошук
        </button>
      </div>

      {/* ====== SIMPLE SEARCH MODE ====== */}
      {!isAdvancedSearch && (
        <div className="search-form__simple-mode">
          <div className="search-form__section">
            <label className="form-label">Пошук лікаря / кабінету</label>
            <div className="keyword-input-wrapper" ref={keywordRef}>
              <input
                type="text"
                className="search-form__keyword-input"
                placeholder=""
                value={keyword}
                onChange={onKeywordChange}
                onFocus={onKeywordFocus}
                onKeyDown={(e) => { if (e.key === 'Enter') { setShowKeywordDropdown(false); handleSearch(); } }}
                autoComplete="off"
              />
              {keyword && (
                <button
                  className="keyword-clear-button"
                  type="button"
                  onClick={() => { setKeyword(''); setShowKeywordDropdown(false); }}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              {showKeywordDropdown && (
                <div className="keyword-dropdown">
                  {popularSearches.length > 0 ? (
                    <>
                      {popularSearches.map(item => (
                        <div
                          key={item.id}
                          className="keyword-dropdown__item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setKeyword(item.text);
                            setShowKeywordDropdown(false);
                          }}
                        >
                          {item.text}
                        </div>
                      ))}
                      {popularSearchesHasMore && (
                        <button
                          className="keyword-dropdown__more"
                          type="button"
                          disabled={popularSearchesLoading}
                          onMouseDown={(e) => { e.preventDefault(); loadMorePopularSearches(); }}
                        >
                          {popularSearchesLoading ? (
                            <><i className="fas fa-spinner fa-spin"></i> Завантаження...</>
                          ) : 'Завантажити ще'}
                        </button>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
            <div className="search-form__hint">
              Вкажіть ПІБ лікаря, спеціальність або назву процедури. Для детального пошуку використовуйте вкладку «Розширений пошук».
            </div>
          </div>
          <div className="search-form__bottom">
            <div className="search-form__filters-wrapper">
              <div className="search-form__filters">
                <label className="filters-label">Показати слоти:</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="onlyAvailable"
                    checked={filters.onlyAvailable}
                    onChange={handleFilterChange}
                  />
                  <span>Вільні слоти</span>
                </label>
                <label className={`checkbox-label ${filters.screening_40_plus ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    name="byReferral"
                    checked={filters.byReferral}
                    onChange={handleFilterChange}
                    disabled={filters.screening_40_plus}
                  />
                  <span>{isMobile() ? 'Направлення' : 'За направленням'}</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="screening_40_plus"
                    checked={filters.screening_40_plus}
                    onChange={handleFilterChange}
                  />
                  <span>Скринінг 40+</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="defencer_benefit"
                    checked={filters.defencer_benefit}
                    onChange={handleFilterChange}
                  />
                  <span>Пільги Захисників</span>
                </label>
                {process.env.REACT_APP_ENV.toLowerCase() !== 'production' && (
                  <label className="checkbox-label" onClick={(e) => {
                    history.push('/paid');
                    e.preventDefault();
                  }} style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="paidConsultations"
                      onClick={(e) => e.preventDefault()}
                    />
                    <span>{isMobile() ? 'Платні слоти' : 'Платні слоти'}</span>
                  </label>
                )}
              </div>
            </div>
            <button
              className="search-form__submit btn btn-primary buy"
              onClick={handleSearch}
            >
              Пошук
            </button>
          </div>
        </div>
      )}

      {/* ====== ADVANCED SEARCH MODE ====== */}
      {isAdvancedSearch && (
        <>
          <div className="search-form__section">
            <label className="form-label">Заклад охорони здоров'я</label>
            {isMobile() ? (
              <div 
                className="mobile-select-trigger"
                onClick={() => openMobileModal('institution')}
              >
                <span className="mobile-select-text" style={{ color: valueInstitution ? '#1767c8' : '#333' }}>
                  {valueInstitution ? 
                    `${valueInstitution.name} (${valueInstitution.full_name}${valueInstitution.code ? ' ' + valueInstitution.code : ''})` : 
                    'Оберіть лікарню'
                  }
                </span>
                <div className="mobile-select-actions">
                  {valueInstitution && (
                    <button 
                      className="mobile-clear-button"
                      onClick={(e) => clearMobileValue('institution', e)}
                      type="button"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                  <i className="fas fa-chevron-down mobile-dropdown-icon"></i>
                </div>
              </div>
            ) : (
              <AsyncPaginate
                {...asyncPaginateMenuProps}
                key={JSON.stringify([user?.city?.id])}
                value={valueInstitution}
                inputValue={institutionSearchQuery}
                onInputChange={setInstitutionSearchQuery}
                onMenuOpen={onInstitutionMenuOpen}
                onMenuClose={onInstitutionMenuClose}
                loadOptions={loadOptionsInstitution}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name + ' (' + option.full_name + (option.code != null ? ' ' + option.code : '') + ')'}
                onChange={onChangeInstitution}
                loadingMessage={() => "Завантаження..."}
                noOptionsMessage={() => "Немає варіантів"}
                clearValue
                isSearchable={true}
                isClearable={true}
                placeholder="Оберіть лікарню"
                additional={{
                  page: 1,
                }}
              />
            )}
          </div>

          {valueInstitution && (
            <div className="search-form__section">
              <label className="form-label">Місце надання послуг</label>
              {isMobile() ? (
                <div 
                  className="mobile-select-trigger"
                  onClick={() => openMobileModal('division')}
                >
                  <span className="mobile-select-text" style={{ color: valueDivision ? '#1767c8' : '#333' }}>
                    {valueDivision ? 
                      (valueDivision.address?.display_text ? 
                        `${valueDivision.name} (${valueDivision?.address?.display_text})` : 
                        valueDivision.name
                      ) : 'Всі'
                    }
                  </span>
                  <div className="mobile-select-actions">
                    {valueDivision && (
                      <button 
                        className="mobile-clear-button"
                        onClick={(e) => clearMobileValue('division', e)}
                        type="button"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <i className="fas fa-chevron-down mobile-dropdown-icon"></i>
                  </div>
                </div>
              ) : (
                <AsyncPaginate
                  {...asyncPaginateMenuProps}
                  key={JSON.stringify([user?.city?.id, valueInstitution?.id])}
                  value={valueDivision}
                  inputValue={divisionSearchQuery}
                  onInputChange={setDivisionSearchQuery}
                  onMenuOpen={onDivisionMenuOpen}
                  onMenuClose={onDivisionMenuClose}
                  loadOptions={loadOptionsDivisions}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.address?.display_text ? `${option.name} (${option.address.display_text})` : option.name}
                  onChange={onChangeDivision}
                  loadingMessage={() => "Завантаження..."}
                  noOptionsMessage={() => "Немає варіантів"}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Всі"
                  additional={{
                    page: 1,
                  }}
                />
              )}
            </div>
          )}

          <div className="search-form__grid">
            <div className="search-form__section">
              <label className="form-label">Спеціальність</label>
              {isMobile() ? (
                <div 
                  className="mobile-select-trigger"
                  onClick={() => openMobileModal('specialization')}
                >
                  <span className="mobile-select-text" style={{ color: valueSpecialization ? '#1767c8' : '#333' }}>
                    {valueSpecialization ? valueSpecialization.name : 'Оберіть спеціалізацію'}
                  </span>
                  <div className="mobile-select-actions">
                    {valueSpecialization && (
                      <button 
                        className="mobile-clear-button"
                        onClick={(e) => clearMobileValue('specialization', e)}
                        type="button"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <i className="fas fa-chevron-down mobile-dropdown-icon"></i>
                  </div>
                </div>
              ) : (
                <AsyncPaginate
                  {...asyncPaginateMenuProps}
                  key={JSON.stringify([user?.city?.id, valueInstitution, valueDivision])}
                  value={valueSpecialization}
                  inputValue={specializationSearchQuery}
                  onInputChange={setSpecializationSearchQuery}
                  onMenuOpen={onSpecializationMenuOpen}
                  onMenuClose={onSpecializationMenuClose}
                  loadOptions={loadOptionsSpecializations}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  onChange={onChangeSpecialization}
                  loadingMessage={() => "Завантаження..."}
                  noOptionsMessage={() => "Немає варіантів"}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Оберіть спеціалізацію"
                  additional={{
                    page: 1,
                  }}
                />
              )}
            </div>

            <div className="search-form__section">
              <label className="form-label">Лікар</label>
              {isMobile() ? (
                <div 
                  className="mobile-select-trigger"
                  onClick={() => openMobileModal('doctor')}
                >
                  <span className="mobile-select-text" style={{ color: valueDoctor ? '#1767c8' : '#333' }}>
                    {valueDoctor ? valueDoctor.full_name : 'Оберіть лікаря'}
                  </span>
                  <div className="mobile-select-actions">
                    {valueDoctor && (
                      <button 
                        className="mobile-clear-button"
                        onClick={(e) => clearMobileValue('doctor', e)}
                        type="button"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <i className="fas fa-chevron-down mobile-dropdown-icon"></i>
                  </div>
                </div>
              ) : (
                <AsyncPaginate
                  {...asyncPaginateMenuProps}
                  key={JSON.stringify([user?.city?.id, valueInstitution, valueDivision, valueSpecialization])}
                  value={valueDoctor}
                  inputValue={doctorSearchQuery}
                  onInputChange={setDoctorSearchQuery}
                  onMenuOpen={onDoctorMenuOpen}
                  onMenuClose={onDoctorMenuClose}
                  loadOptions={loadOptionsDoctors}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.full_name}
                  onChange={onChangeDoctor}
                  loadingMessage={() => "Завантаження..."}
                  noOptionsMessage={() => "Немає варіантів"}
                  isSearchable={true}
                  isClearable={true}
                  placeholder="Оберіть лікаря"
                  additional={{
                    page: 1,
                  }}
                />
              )}
            </div>

            <div className="search-form__section">
              <label className="form-label">Пошук текстом (послуга, кабінет)</label>
              <div className="keyword-input-wrapper" ref={keywordRef}>
                <input
                  type="text"
                  className="search-form__keyword-input"
                  placeholder="Введіть текст або оберіть варіант"
                  value={keyword}
                  onChange={onKeywordChange}
                  onFocus={onKeywordFocus}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setShowKeywordDropdown(false); handleSearch(); } }}
                  autoComplete="off"
                />
                {keyword && (
                  <button
                    className="keyword-clear-button"
                    type="button"
                    onClick={() => { setKeyword(''); setShowKeywordDropdown(false); }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
                {showKeywordDropdown && (
                  <div className="keyword-dropdown">
                    {popularSearchesLoading && popularSearches.length === 0 ? (
                      <></>
                    ) : popularSearches.length > 0 ? (
                      <>
                        {popularSearches.map(item => (
                          <div
                            key={item.id}
                            className="keyword-dropdown__item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setKeyword(item.text);
                              setShowKeywordDropdown(false);
                            }}
                          >
                            {item.text}
                          </div>
                        ))}
                        {popularSearchesHasMore && (
                          <button
                            className="keyword-dropdown__more"
                            type="button"
                            disabled={popularSearchesLoading}
                            onMouseDown={(e) => { e.preventDefault(); loadMorePopularSearches(); }}
                          >
                            {popularSearchesLoading ? (
                              <><i className="fas fa-spinner fa-spin"></i> Завантаження...</>
                            ) : 'Завантажити ще'}
                          </button>
                        )}
                      </>
                    ) : !popularSearchesLoading ? (
                      <></>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="search-form__bottom">
            <div className="search-form__filters-wrapper">
              <div className="search-form__filters">
                <label className="filters-label">Показати слоти:</label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="onlyAvailable"
                    checked={filters.onlyAvailable}
                    onChange={handleFilterChange}
                  />
                  <span>Вільні слоти</span>
                </label>
                <label className={`checkbox-label ${filters.screening_40_plus ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    name="byReferral"
                    checked={filters.byReferral}
                    onChange={handleFilterChange}
                    disabled={filters.screening_40_plus}
                  />
                  <span>{isMobile() ? 'Направлення' : 'За направленням'}</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="screening_40_plus"
                    checked={filters.screening_40_plus}
                    onChange={handleFilterChange}
                  />
                  <span>Скринінг 40+</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="defencer_benefit"
                    checked={filters.defencer_benefit}
                    onChange={handleFilterChange}
                  />
                  <span>Пільги Захисників</span>
                </label>
                {process.env.REACT_APP_ENV.toLowerCase() !== 'production' && (
                  <label className="checkbox-label" onClick={(e) => {
                    history.push('/paid');
                    e.preventDefault();
                  }} style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="paidConsultations"
                      onClick={(e) => e.preventDefault()}
                    />
                    <span>{isMobile() ? 'Платні слоти' : 'Платні слоти'}</span>
                  </label>
                )}
              </div>
            </div>
            <button 
              className="search-form__submit btn btn-primary buy"
              onClick={handleSearch}
            >
              Пошук
            </button>
          </div>
        </>
      )}

      {/* Mobile Modal */}
      {showMobileModal && (
        <div className="mobile-select-modal">
          <div className="mobile-select-modal-content">
            <div className="mobile-select-header">
              <h3>
                {showMobileModal === 'institution' && 'Оберіть лікарню'}
                {showMobileModal === 'division' && 'Місця надання послуг'}
                {showMobileModal === 'specialization' && 'Оберіть спеціалізацію'}
                {showMobileModal === 'doctor' && 'Оберіть лікаря'}
              </h3>
              <button className="mobile-select-close" onClick={closeMobileModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="mobile-select-search">
              <input
                type="text"
                placeholder="Пошук..."
                value={mobileSearchQuery}
                onChange={(e) => handleMobileSearch(e.target.value)}
                className="mobile-search-input"
                autoFocus
              />
            </div>
            <div className="mobile-select-list">
              {mobileLoading && mobileOptions.length === 0 ? (
                <div className="mobile-select-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Завантаження...</span>
                </div>
              ) : mobileOptions.length > 0 ? (
                <>
                  {mobileOptions.map((option) => (
                    <div
                      key={option.id}
                      className="mobile-select-option"
                      onClick={() => selectMobileOption(option)}
                    >
                      {showMobileModal === 'institution' ? 
                        `${option.name} (${option.full_name}${option.code ? ' ' + option.code : ''})` :
                        showMobileModal === 'doctor' ? option.full_name :
                        showMobileModal === 'division' && option.address?.display_text ? 
                        `${option.name} (${option.address.display_text})` : option.name
                      }
                    </div>
                  ))}
                  {mobileHasMore && (
                    <div className="mobile-select-load-more">
                      <button onClick={loadMoreMobileOptions} disabled={mobileLoading}>
                        {mobileLoading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Завантаження...</span>
                          </>
                        ) : (
                          'Завантажити ще'
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mobile-select-empty">
                  Немає варіантів
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .search-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1rem;
          max-width: 100%;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(28, 92, 170, 0.10);
          border: 1px solid #e0e8f5;
          padding: 1.25rem 1.5rem 1.25rem;
        }
        .search-form__grid {
          display: grid;
          gap: 1rem;
        }
        @media (min-width: 1000px) {
          .search-form__grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        .search-form__section {
          display: flex;
          flex-direction: column;
          min-width: 0;
          overflow: visible;
        }
        .form-label {
          font-weight: 500;
          color: #333;
          margin-bottom: 0rem;
          font-size: 0.9rem;
        }
        .search-form__bottom {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        @media (min-width: 1000px) {
          .search-form__bottom {
            flex-direction: row;
            align-items: flex-start;
            justify-content: space-between;
            gap: 2rem;
          }
          .search-form__filters-wrapper {
            flex: 1;
          }
        }
        .search-form__filters-wrapper {
          width: 100%;
        }
        .search-form__filters {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .search-form__submit {
          align-self: flex-end;
          min-width: 150px;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 500;
          border-radius: 8px;
          border: none;
          color: white;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .mobile .search-form__submit {
          align-self: stretch;
          padding: 0.7rem;
          font-size: 1.1rem;
          border-radius: 12px;
          margin-top: 0rem;
        }
        .search-form__bottom {
          display: flex;
          align-items: flex-start;
        }
        .search-form__filters {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          flex-wrap: wrap;
        }
        .filters-label {
          font-weight: 500;
          color: #333;
          margin-right: 1rem;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }
        @media (max-width: 1000px) and (min-width: 375px) {
          .filters-label {
            width: 100%;
            margin-bottom: 0rem;
          }
          .search-form__filters {
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem;
            justify-content: space-between;
          }
        }
        @media (max-width: 1000px) {
          .checkbox-label {
            flex: 1 1 auto;
            width: calc(50% - 0.5rem);
          }
        }
        
        /* Custom Checkbox Styles */
        .checkbox-label {
          display: flex;
          align-items: center;
          text-align: center;
          gap: 0.75rem;
          cursor: pointer;
          font-size: 0.95rem;
          line-height: 1.4;
          position: relative;
        }
        
        .checkbox-label input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
        }
        
        .checkbox-label::before {
          content: '';
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 2px solid #ddd;
          border-radius: 4px;
          background: white;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        
        .checkbox-label:hover::before {
          border-color: #1c5caa;
        }
        
        .checkbox-label input[type="checkbox"]:checked + span::before,
        .checkbox-label:has(input[type="checkbox"]:checked)::before {
          background: #1c5caa;
          border-color: #1c5caa;
        }
        
        .checkbox-label:has(input[type="checkbox"]:checked)::after {
          content: '✓';
          position: absolute;
          left: 5px;
          top: 52%;
          transform: translateY(-50%);
          color: white;
          font-size: 14px;
          font-weight: bold;
        }
        
        .checkbox-label.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .checkbox-label.disabled::before {
          background: #f5f5f5;
          border-color: #d9d9d9;
        }
        
        .checkbox-label.disabled:hover::before {
          border-color: #d9d9d9;
        }
        
        @media (max-width: 1000px) {
          .checkbox-label {
            padding: 0.5rem;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #e9ecef;
            margin: 0;
          }
          
          .checkbox-label::before {
            width: 22px;
            height: 22px;
            border-radius: 6px;
          }
          
          .checkbox-label:has(input[type="checkbox"]:checked)::after {
            left: 14px;
            font-size: 14px;
          }
        }
        
        .date-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .date-icon {
          position: absolute;
          right: 1rem;
          color: #666;
          font-size: 1.1rem;
          pointer-events: none;
          z-index: 1;
        }
        
        .date-clear-button {
          position: absolute;
          right: 2.5rem;
          background: none;
          border: none;
          color: #999;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          z-index: 2;
        }
        
        .date-clear-button:hover {
          background-color: rgba(0, 0, 0, 0.1);
          color: #666;
        }
        
        .date-clear-button:active {
          background-color: rgba(0, 0, 0, 0.15);
        }
        
        .search-form__date-picker {
          width: 100%;
          padding: 0 0.75rem;
          border: 1px solid #ccc;
          border-radius: 5px;
          font-family: inherit;
          font-size: 1rem;
          background: white;
          color: #333;
          height: auto;
          min-height: 38px;
          -webkit-appearance: none;
          -moz-appearance: textfield;
          appearance: none;
          cursor: pointer;
        }
        
        .search-form__date-picker:focus {
          outline: none;
          border-color: #1c5caa;
          box-shadow: 0 0 0 3px rgba(28, 92, 170, 0.1);
        }
        
        @media (max-width: 1000px) {
          .search-form__date-picker {
            padding: 0.5rem;
            font-size: 1.1rem;
            border-radius: 12px;
            min-height: 40px;
            background: white;
            position: relative;
          }
          .main-title-wrapper .title {
            font-size: 21px;
            margin-bottom: 0.5rem;
            margin-top: 0.75rem;
          }
          
          .search-form__date-picker::-webkit-calendar-picker-indicator {
            background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23333' d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM2 2a1 1 0 0 0-1 1v1h14V3a1 1 0 0 0-1-1H2zm13 3H1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5z'/%3e%3c/svg%3e") no-repeat;
            background-size: 20px;
            width: 20px;
            height: 20px;
            cursor: pointer;
          }
          
          .date-clear-button {
            right: 3rem;
            width: 28px;
            height: 28px;
            font-size: 1rem;
          }
        }
        
        label {
          margin-bottom: 0.5rem;
        }
        
        /* Mobile Select Styles */
        .mobile-select-trigger {
          width: 100%;
          max-width: 100%;
          padding: 0.5rem;
          border: 1px solid #ccc;
          border-radius: 12px;
          background: white;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          min-height: 40px;
          color: #333;
          font-size: 1.1rem;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
          position: relative;
        }
        
        .mobile-select-trigger:hover,
        .mobile-select-trigger:active {
          border-color: #1c5caa;
          box-shadow: 0 0 0 3px rgba(28, 92, 170, 0.1);
        }
        
        .mobile-select-trigger i {
          color: #666;
          font-size: 1rem;
        }
        
        .mobile-select-text {
          flex: 1;
          min-width: 0;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding-right: 0.5rem;
        }
        
        .mobile-select-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-shrink: 0;
        }
        
        .mobile-clear-button {
          background: none;
          border: none;
          color: #999;
          font-size: 1rem;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 50%;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
        }
        
        .mobile-clear-button:hover {
          background-color: rgba(0, 0, 0, 0.1);
          color: #666;
        }
        
        .mobile-dropdown-icon {
          color: #666;
          font-size: 1rem;
        }
        
        .mobile-select-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1010;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          backdrop-filter: blur(3px);
        }
        
        .mobile-select-modal-content {
          background: white;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          animation: slideInUp 0.3s ease-out;
        }
        
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        .mobile-select-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 1.25rem;
          border-bottom: 1px solid #eee;
          background: #f8f9fa;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .mobile-select-header h3 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
          color: #333;
        }
        
        .mobile-select-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #1c5caa;
          padding: 0.75rem;
          border-radius: 50%;
          transition: background-color 0.2s;
        }
        
        .mobile-select-close:hover {
          background-color: rgba(28, 92, 170, 0.1);
        }
        
        .mobile-select-search {
          padding: 1.25rem;
          border-bottom: 1px solid #eee;
          background: white;
          position: sticky;
          top: 73px;
          z-index: 9;
        }
        
        .mobile-search-input {
          width: 100%;
          padding: 1rem;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 1.1rem;
          background: #f8f9fa;
          transition: all 0.2s ease;
        }
        
        .mobile-search-input:focus {
          outline: none;
          border-color: #1c5caa;
          background: white;
          box-shadow: 0 0 0 3px rgba(28, 92, 170, 0.1);
        }
        
        .mobile-select-list {
          flex: 1;
          overflow-y: auto;
          padding: 0;
          -webkit-overflow-scrolling: touch;
        }
        
        .mobile-select-option {
          padding: 1.25rem;
          border-bottom: 1px solid #f0f0f0;
          cursor: pointer;
          transition: background-color 0.2s;
          font-size: 1.05rem;
          line-height: 1.4;
        }
        
        .mobile-select-option:last-child {
          border-bottom: none;
        }
        
        .mobile-select-option:hover {
          background-color: #f8f9fa;
        }
        
        .mobile-select-option:active {
          background-color: #e9ecef;
        }
        
        .mobile-select-loading, .mobile-select-empty {
          padding: 3rem 2rem;
          text-align: center;
          color: #666;
          font-size: 1.1rem;
        }
        
        .mobile-select-loading i {
          margin-right: 0.75rem;
          font-size: 1.25rem;
        }
        
        .mobile-select-load-more {
          padding: 1.5rem;
          text-align: center;
          background: #f8f9fa;
          border-top: 1px solid #eee;
        }
        
        .mobile-select-load-more button {
          background: #1c5caa;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.05rem;
          font-weight: 500;
          transition: all 0.2s ease;
          min-width: 160px;
        }
        
        .mobile-select-load-more button:hover {
          background: #174a91;
        }
        
        .mobile-select-load-more button:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }
        
        .mobile-select-load-more button i {
          margin-right: 0.5rem;
        }
        
        /* Additional mobile optimizations */
        @media (max-width: 1000px) {
          .search-form {
            gap: 0.5rem;
            padding: 0.75rem 0.75rem 1rem;
            border-radius: 10px;
            margin: 15px;
          }
          .tabs-wrapper .search-form {
            margin: 0;
        }
          
          .search-form__section {
            gap: 0.1rem;
          }
          
          .form-label {
            font-size: 1rem;
            font-weight: 600;
            color: #333;
          }
          
          .search-form__grid {
            gap: 0.3rem;
          }
          
          .search-form__bottom {
            gap: 0.5rem;
            margin-top: 0.5rem;
          }
        }

        /* Simple search mode */
        .search-form__simple-mode {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .search-form__hint {
          font-size: 0.85rem;
          color: #666;
          margin-top: 0.4rem;
          line-height: 1.4;
        }

        .search-form__advanced-toggle {
          background: none;
          border: none;
          color: #1c5caa;
          cursor: pointer;
          font-size: inherit;
          padding: 0;
          text-decoration: underline;
          font-family: inherit;
        }

        .search-form__advanced-toggle:hover {
          color: #174a91;
        }

        /* Tabs */
        .search-form__tabs {
          display: flex;
          border-bottom: 2px solid #e9ecef;
          margin-bottom: 0.25rem;
        }

        .search-form__tab {
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          padding: 0.5rem 1.25rem;
          font-size: 0.95rem;
          font-family: inherit;
          font-weight: 500;
          color: #666;
          cursor: pointer;
          transition: color 0.15s, border-color 0.15s;
          white-space: nowrap;
        }

        .search-form__tab:hover {
          color: #1c5caa;
        }

        .search-form__tab--active {
          color: #1c5caa;
          border-bottom-color: #1c5caa;
          background: #e8f0fb;
          border-radius: 6px 6px 0 0;
        }

        @media (max-width: 1000px) {
          .search-form__tabs {
            margin-bottom: 0.1rem;
          }
          .search-form__tab {
            flex: 1;
            text-align: center;
            font-size: 1rem;
            padding: 0.65rem 0.5rem;
          }
          .search-form__hint {
            font-size: 0.95rem;
          }
        }

        /* Keyword input styles */
        .keyword-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-form__keyword-input {
          width: 100%;
          padding: 0 0.75rem;
          border: 1px solid #ccc;
          border-radius: 5px;
          font-family: inherit;
          font-size: 1rem;
          background: white;
          color: #1767c8;
          height: auto;
          min-height: 38px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .search-form__keyword-input:focus {
          border-color: #1c5caa;
          box-shadow: 0 0 0 3px rgba(28, 92, 170, 0.1);
        }

        .keyword-clear-button {
          position: absolute;
          right: 0.6rem;
          background: none;
          border: none;
          color: #999;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          transition: all 0.2s ease;
          z-index: 2;
        }

        .keyword-clear-button:hover {
          background-color: rgba(0, 0, 0, 0.1);
          color: #666;
        }

        .keyword-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: white;
          border-radius: 5px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          z-index: 2001;
          max-height: 260px;
          overflow-y: auto;
        }

        .keyword-dropdown__item {
          padding: 0.6rem 0.75rem;
          cursor: pointer;
          font-size: 0.95rem;
          color: #333;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.15s;
        }

        .keyword-dropdown__item:last-of-type {
          border-bottom: none;
        }

        .keyword-dropdown__item:hover {
          background-color: #f0f5ff;
          color: #1c5caa;
        }

        .keyword-dropdown__loading,
        .keyword-dropdown__empty {
          padding: 0.75rem;
          font-size: 0.9rem;
          color: #888;
          text-align: center;
        }

        .keyword-dropdown__hint {
          padding: 0.5rem 0.75rem;
          font-size: 0.8rem;
          color: #888;
          border-bottom: 1px solid #f0f0f0;
        }

        .keyword-dropdown__more {
          display: block;
          width: 100%;
          padding: 0.6rem;
          background: none;
          border: none;
          border-top: 1px solid #eee;
          cursor: pointer;
          font-size: 0.9rem;
          color: #1c5caa;
          transition: background-color 0.15s;
        }

        .keyword-dropdown__more:hover {
          background-color: #f0f5ff;
        }

        .keyword-dropdown__more:disabled {
          color: #aaa;
          cursor: not-allowed;
        }

        @media (max-width: 1000px) {
          .search-form__keyword-input {
            padding: 0.5rem;
            font-size: 1.1rem;
            border-radius: 12px;
            min-height: 40px;
          }
        }
      `}</style>
    </div>
  );
};

const mapStateToProps = ({ user, search, specializations, patient }) => ({ user, search, specializations, patient });

const mapDispatchToProps = { onSearch: search, getSpecializations, searchFocusedToggle };

const SearchFormContainer = connect(mapStateToProps, mapDispatchToProps)(SearchForm);

export default SearchFormContainer;
